import {
  ConfirmedOption,
  EXPORT_OK_STATES,
  ExtractionConfidence,
  FieldCondition,
  FieldFill,
  FieldValue,
  FillState,
  GfdPlaybook,
  OPTIONAL_FIELD_IDS,
  PacketField,
  PlaybookField,
  QUEUE_STATES,
  SeedInitiative,
  SourceCitation,
  SourceDocument,
} from './types';

export const CONFIRMATIONS_KEY = 'gfd_playbook_confirmations_v1';
export const PACKET_EDITS_KEY = 'gfd_packet_edits_v2';

const LEGACY: Record<ExtractionConfidence, FillState> = {
  high: 'confirmed',
  medium: 'inferred',
  low: 'best_guess',
};

export function canonicalFill(raw: Partial<FieldFill> & { fieldId: string }): FieldFill {
  const value = raw.value === undefined || raw.value === null ? '' : raw.value;
  let state: FillState = raw.state || (raw.extractionConfidence ? LEGACY[raw.extractionConfidence] : undefined) || 'not_found';
  if ((value === '' || value === '—' || (Array.isArray(value) && value.length === 0)) && state !== 'conflict' && state !== 'user_confirmed') {
    state = 'not_found';
  }
  const sourceCitations: SourceCitation[] = Array.isArray(raw.sourceCitations) && raw.sourceCitations.length
    ? raw.sourceCitations
    : (raw.sourceDocId || raw.sourceSnippet)
      ? [{ docName: String(raw.sourceDocId || ''), locator: '', snippet: String(raw.sourceSnippet || '') }]
      : [];
  return {
    fieldId: raw.fieldId,
    value,
    state,
    candidates: raw.candidates || [],
    sourceCitations,
    rationale: raw.rationale || '',
    lastUserEditAt: raw.lastUserEditAt || null,
    confirmInServiceNow: Boolean(raw.confirmInServiceNow),
    flaggedReview: Boolean(raw.flaggedReview),
    extractionConfidence: raw.extractionConfidence,
    sourceDocId: raw.sourceDocId || sourceCitations[0]?.docName,
    sourceSnippet: raw.sourceSnippet || sourceCitations[0]?.snippet,
  };
}

export function valueToString(value: FieldValue | undefined): string {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

export function parseMultiValue(value: FieldValue | undefined): string[] {
  if (value === undefined || value === null) return [];
  if (Array.isArray(value)) return value.map(String);
  return String(value)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

export function conditionMet(
  condition: FieldCondition | undefined,
  values: Record<string, FieldValue>
): boolean {
  if (!condition) return true;
  const current = values[condition.fieldId];
  if (condition.equals !== undefined) {
    if (Array.isArray(condition.equals) || Array.isArray(current)) {
      const want = parseMultiValue(condition.equals as FieldValue);
      const have = parseMultiValue(current);
      return want.some((w) => have.includes(w)) || valueToString(current) === valueToString(condition.equals);
    }
    return valueToString(current) === valueToString(condition.equals);
  }
  if (condition.notEquals !== undefined) {
    return valueToString(current) !== valueToString(condition.notEquals);
  }
  if (condition.notEqualsAny !== undefined) {
    return !condition.notEqualsAny.includes(valueToString(current));
  }
  if (condition.includes !== undefined) {
    return parseMultiValue(current).includes(condition.includes);
  }
  return true;
}

export function isFieldVisible(
  field: PlaybookField,
  values: Record<string, FieldValue>
): boolean {
  return conditionMet(field.showWhen, values);
}

export function fillsToValueMap(fills: FieldFill[]): Record<string, FieldValue> {
  return fills.reduce<Record<string, FieldValue>>((acc, fill) => {
    acc[fill.fieldId] = canonicalFill(fill).value;
    return acc;
  }, {});
}

export function loadConfirmations(): ConfirmedOption[] {
  try {
    const raw = localStorage.getItem(CONFIRMATIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveConfirmation(fieldId: string, value: string): ConfirmedOption[] {
  const next = loadConfirmations().filter(
    (row) => !(row.fieldId === fieldId && row.value === value)
  );
  next.push({ fieldId, value, confirmedAt: new Date().toISOString() });
  try {
    localStorage.setItem(CONFIRMATIONS_KEY, JSON.stringify(next));
  } catch {
    /* private mode / quota */
  }
  return next;
}

export function applyConfirmationsToPlaybook(
  playbook: GfdPlaybook,
  confirmations: ConfirmedOption[]
): GfdPlaybook {
  if (!confirmations.length) return playbook;
  const confirmed = new Set(confirmations.map((c) => `${c.fieldId}::${c.value}`));
  return {
    ...playbook,
    sections: playbook.sections.map((section) => ({
      ...section,
      fields: section.fields.map((field) => ({
        ...field,
        options: field.options?.map((option) =>
          confirmed.has(`${field.id}::${option.value}`)
            ? { ...option, confidence: 'confirmed' as const }
            : option
        ),
      })),
    })),
  };
}

export function buildPacket(
  playbook: GfdPlaybook,
  seed: SeedInitiative,
  edits: Record<string, FieldValue> = {}
): PacketField[] {
  const fills = (seed.fills || []).map((f) => canonicalFill(f));
  const base = fillsToValueMap(fills);
  const values: Record<string, FieldValue> = { ...base, ...edits };
  const fillById = new Map(fills.map((fill) => [fill.fieldId, fill]));
  const docs = new Map(seed.documents.map((doc) => [doc.id, doc]));

  return playbook.sections.flatMap((section) =>
    section.fields.map((field) => {
      const fill = fillById.get(field.id);
      const value = values[field.id] ?? fill?.value ?? '';
      const edited = Object.prototype.hasOwnProperty.call(edits, field.id);
      const state: FillState = edited ? 'user_confirmed' : (fill?.state || 'not_found');
      const citations = fill?.sourceCitations || [];
      return {
        field,
        value,
        state,
        extractionConfidence: fill?.extractionConfidence,
        sourceDoc: fill ? docs.get(fill.sourceDocId || citations[0]?.docName || '') : undefined,
        sourceSnippet: fill?.sourceSnippet || citations[0]?.snippet || '',
        sourceCitations: citations,
        candidates: fill?.candidates || [],
        rationale: fill?.rationale ?? '',
        confirmInServiceNow: fill?.confirmInServiceNow ?? field.playbookConfidence !== 'confirmed',
        flaggedReview: fill?.flaggedReview,
        visible: isFieldVisible(field, values),
      };
    })
  );
}

export function visiblePacketFields(packet: PacketField[]): PacketField[] {
  return packet.filter((row) => row.visible);
}

export function loadEdits(initiativeId?: string): Record<string, FieldValue> {
  if (!initiativeId) return {};
  try {
    const raw = localStorage.getItem(PACKET_EDITS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const scoped = parsed[initiativeId];
    return scoped && typeof scoped === 'object' && !Array.isArray(scoped) ? scoped : {};
  } catch {
    return {};
  }
}

export function saveEdits(initiativeId: string, edits: Record<string, FieldValue>): void {
  try {
    const raw = localStorage.getItem(PACKET_EDITS_KEY);
    const all = raw ? JSON.parse(raw) : {};
    const next = all && typeof all === 'object' ? all : {};
    next[initiativeId] = edits;
    localStorage.setItem(PACKET_EDITS_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

export function isEmptyValue(value: FieldValue | undefined): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return !value.trim() || value === '—';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

export function isRequiredVisible(row: PacketField): boolean {
  if (!row.visible) return false;
  if (OPTIONAL_FIELD_IDS.has(row.field.id)) return false;
  if (row.field.required === false) return false;
  return true;
}

export function packetGaps(packet: PacketField[]): {
  empty: PacketField[];
  low: PacketField[];
  confirm: PacketField[];
  queue: PacketField[];
  hidden: number;
} {
  const visible = visiblePacketFields(packet);
  const queue = visible.filter((row) => isRequiredVisible(row) && QUEUE_STATES.includes(row.state));
  return {
    empty: visible.filter((row) => isRequiredVisible(row) && (isEmptyValue(row.value) || row.state === 'not_found')),
    low: visible.filter((row) => row.state === 'best_guess'),
    confirm: visible.filter((row) => row.confirmInServiceNow || row.state === 'conflict'),
    queue,
    hidden: packet.filter((row) => !row.visible).length,
  };
}

export function compactFieldSchema(playbook: GfdPlaybook) {
  return playbook.sections.flatMap((section) =>
    section.fields.map((field) => ({
      id: field.id,
      label: field.label,
      fieldType: field.fieldType,
      required: field.required,
      playbookConfidence: field.playbookConfidence,
      options: field.options,
      optionsNote: field.optionsNote || undefined,
      showWhen: field.showWhen,
    }))
  );
}

export function initiativeFromGenerate(args: {
  name?: string;
  summary?: string;
  notes?: string;
  championName?: string;
  championTitle?: string;
  documents?: SourceDocument[];
  fills?: FieldFill[];
  id?: string;
}): SeedInitiative {
  const uploaded = args.documents ? [...args.documents] : [];
  if ((args.notes || '').trim() && !uploaded.some((d) => d.id === 'notes')) {
    uploaded.push({
      id: 'notes',
      name: 'Consultant notes',
      kind: 'Notes',
      summary: 'Additional context entered in Jarvis',
      excerpt: (args.notes || '').slice(0, 1200),
    });
  }
  return {
    id: args.id || `generated-${Date.now()}`,
    name: args.name || 'Untitled GFD initiative',
    client: 'SMBC',
    champion: {
      name: args.championName || 'Internal champion',
      title: args.championTitle || '',
      note: 'Prepared from uploaded documents and consultant notes.',
    },
    consultantNotes: args.summary || args.notes || '',
    documents: uploaded.length
      ? uploaded
      : [{
          id: 'notes',
          name: 'Consultant notes',
          kind: 'Notes',
          summary: 'Additional context entered in Jarvis',
          excerpt: args.notes || '',
        }],
    fills: (args.fills || []).map((f) => canonicalFill(f as FieldFill)),
  };
}

export function packetStats(packet: PacketField[]): {
  total: number;
  confirmInSn: number;
  high: number;
  medium: number;
  low: number;
  confirmed: number;
  inferred: number;
  bestGuess: number;
  notFound: number;
  conflict: number;
  userConfirmed: number;
  hidden: number;
  empty: number;
  unresolvedRequired: number;
} {
  const visible = visiblePacketFields(packet);
  const by = (state: FillState) => visible.filter((row) => row.state === state).length;
  const unresolvedRequired = visible.filter((row) => isRequiredVisible(row) && QUEUE_STATES.includes(row.state)).length;
  return {
    total: visible.length,
    confirmInSn: visible.filter((row) => row.confirmInServiceNow).length,
    confirmed: by('confirmed'),
    inferred: by('inferred'),
    bestGuess: by('best_guess'),
    notFound: by('not_found'),
    conflict: by('conflict'),
    userConfirmed: by('user_confirmed'),
    high: by('confirmed'),
    medium: by('inferred'),
    low: by('best_guess') + by('not_found'),
    hidden: packet.filter((row) => !row.visible).length,
    empty: visible.filter((row) => isRequiredVisible(row) && isEmptyValue(row.value)).length,
    unresolvedRequired,
  };
}

export function exportBlocked(packet: PacketField[]): boolean {
  return visiblePacketFields(packet).some(
    (row) => isRequiredVisible(row) && !EXPORT_OK_STATES.includes(row.state)
  );
}
